// QA doc: features/website/cart.md R-C35 (a page left open across a deploy asks for a lazy file that is gone).

/** What each browser says when `import()` of a lazy file fails because the file is gone (the host answers its 404 page). */
const CHUNK_LOAD_FAILURE_MESSAGES: readonly RegExp[] = [
  /failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /is not a valid javascript mime type/i, // the 404 page served as text/html
  /loading chunk [^ ]+ failed/i // webpack style
];

/** The error's message, or '' when it carries none. `rejection` is where the router and Zone.js keep the original of a wrapped failure. */
const messageOf = (error: unknown): string => {
  if (typeof error === 'string') return '';
  if (typeof error !== 'object' || error === null) return '';
  const { message } = error as { message?: unknown };
  return typeof message === 'string' ? message : '';
};

/** True when `error` (or the failure it wraps) says a lazily loaded file of the site could not be fetched. */
export const isChunkLoadError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  if ((error as { name?: unknown }).name === 'ChunkLoadError') return true;

  const message = messageOf(error);
  if (CHUNK_LOAD_FAILURE_MESSAGES.some((pattern) => pattern.test(message))) return true;

  const wrapped = (error as { rejection?: unknown }).rejection;
  return wrapped !== undefined && wrapped !== error && isChunkLoadError(wrapped);
};

/** The message of a chunk-load error, trimmed for a log line. */
export const chunkLoadErrorMessage = (error: unknown): string => {
  const own = messageOf(error);
  if (own) return own;
  const wrapped = (error as { rejection?: unknown } | null)?.rejection;
  return wrapped === undefined || wrapped === error ? '' : chunkLoadErrorMessage(wrapped);
};

/** The address of the lazy file named in a chunk-load error (Chrome, Edge and Firefox name it; Safari does not), or null. */
export const chunkUrlOf = (error: unknown): string | null => {
  const found = /https?:\/\/[^\s'"()]+?\.m?js\b/i.exec(chunkLoadErrorMessage(error));
  return found ? found[0] : null;
};

/** The `src` of the page's `main-<hash>.js` script in an index.html text, or null. */
export const mainScriptOf = (html: string): string | null =>
  /<script[^>]+src="([^"]*main-[^"]+\.js)"/i.exec(html)?.[1] ?? null;
